import React, { useEffect, Suspense, lazy } from 'react';
import { HomePage } from './pages/Home.jsx';
import './assets/styles/App.css';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Box, CircularProgress } from '@mui/material';
import PersonalShareBanner from './components/sharedComponents/PersonalShareBanner';
import { flushQueue } from './utils/leadApi';
import { installClickTracking } from './utils/track';

// Home stays eager (first paint); everything else is code-split to shrink the initial bundle.
const Persona = lazy(() => import('./pages/Persona.jsx'));
const Mission = lazy(() => import('./pages/Mission.jsx'));
const Blog = lazy(() => import('./pages/Blog.jsx'));
const BlogArticle = lazy(() => import('./pages/BlogArticle.jsx'));
const FindYourEV = lazy(() => import('./pages/FindYourEV.jsx'));
const EVDetail = lazy(() => import('./pages/EVDetail.jsx'));
const Chargers = lazy(() => import('./pages/Chargers.jsx'));
const SavingsCalculator = lazy(() => import('./pages/SavingsCalculator.jsx'));
const EVQuiz = lazy(() => import('./pages/EVQuiz.jsx'));
const IdiotQuiz = lazy(() => import('./pages/IdiotQuiz.jsx'));
const Incentives = lazy(() => import('./pages/Incentives.jsx'));
const StateIncentives = lazy(() => import('./pages/StateIncentives.jsx'));
const Concierge = lazy(() => import('./pages/Concierge.jsx'));
const HowItWorks = lazy(() => import('./pages/HowItWorks.jsx'));
const SurvivalKit = lazy(() => import('./pages/SurvivalKit.jsx'));
const DealChecker = lazy(() => import('./pages/DealChecker.jsx'));
const Brief = lazy(() => import('./pages/Brief.jsx'));
const IdioticStatements = lazy(() => import('./pages/IdioticStatements.jsx'));
const IdioticStatementPage = lazy(() => import('./pages/IdioticStatementPage.jsx'));
const EvVsGas = lazy(() => import('./pages/EvVsGas.jsx'));
const EvVsGasState = lazy(() => import('./pages/EvVsGasState.jsx'));
const EvVsGasCity = lazy(() => import('./pages/EvVsGasCity.jsx'));
const EvCheaperThanGas = lazy(() => import('./pages/EvCheaperThanGas.jsx'));
const EvCompare = lazy(() => import('./pages/EvCompare.jsx'));
const EvComparePage = lazy(() => import('./pages/EvComparePage.jsx'));
const EvRegistrationFees = lazy(() => import('./pages/EvRegistrationFees.jsx'));
const UsedEvs = lazy(() => import('./pages/UsedEvs.jsx'));
const UsedEvModel = lazy(() => import('./pages/UsedEvModel.jsx'));
const Share = lazy(() => import('./pages/Share.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress sx={{ color: '#536674' }} />
  </Box>
);

function App() {
  // Retry any leads that were queued locally while the backend was unreachable.
  useEffect(() => {
    flushQueue();
  }, []);

  // One delegated listener: outbound, internal, download, contact and
  // data-cta clicks on every page (see src/utils/track.js, ANALYTICS.md).
  useEffect(() => installClickTracking(), []);

  return (
    <div className="app-container fi-root">
      <BrowserRouter>
        <ScrollToTop />
        <PersonalShareBanner />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/">
              <Route index element={<HomePage />} />
              <Route path='/persona' element={<Persona />} />
              <Route path='/persona/:idiotType' element={<Persona />} />
              <Route path='/mission' element={<Mission />} />
              <Route path='/blog' element={<Blog />} />
              <Route path='/blog/:slug' element={<BlogArticle />} />
              <Route path='/find-your-ev' element={<FindYourEV />} />
              <Route path='/find-your-ev/:slug' element={<EVDetail />} />
              <Route path='/chargers' element={<Chargers />} />
              <Route path='/savings-calculator' element={<SavingsCalculator />} />
              <Route path='/ev-quiz' element={<EVQuiz />} />
              <Route path='/what-kind-of-idiot' element={<IdiotQuiz />} />
              <Route path='/incentives' element={<Incentives />} />
              <Route path='/incentives/:stateSlug' element={<StateIncentives />} />
              <Route path='/ev-registration-fees' element={<EvRegistrationFees />} />
              <Route path='/used-evs' element={<UsedEvs />} />
              <Route path='/used-evs/:slug' element={<UsedEvModel />} />
              <Route path='/concierge' element={<Concierge />} />
              <Route path='/how-it-works' element={<HowItWorks />} />
              <Route path='/dealership-survival-kit' element={<SurvivalKit />} />
              <Route path='/deal-checker' element={<DealChecker />} />
              <Route path='/brief' element={<Brief />} />
              <Route path='/idiotic-statements' element={<IdioticStatements />} />
              <Route path='/idiotic-statements/:slug' element={<IdioticStatementPage />} />
              <Route path='/ev-vs-gas' element={<EvVsGas />} />
              <Route path='/ev-vs-gas/:slug' element={<EvVsGasState />} />
              <Route path='/ev-vs-gas/:slug/:city' element={<EvVsGasCity />} />
              <Route path='/is-an-ev-cheaper-than-gas' element={<EvCheaperThanGas />} />
              <Route path='/compare' element={<EvCompare />} />
              <Route path='/compare/:pair' element={<EvComparePage />} />
              <Route path='/share' element={<Share />} />
              <Route path='*' element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
